import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Section from "../components/marketing/Section";
import SectionHeading from "../components/marketing/SectionHeading";

/**
 * /terms — kept deliberately consistent with what the product actually does.
 * Two clauses in particular are load-bearing and must not drift from the
 * code:
 *
 *   §6 "no refunds for partial periods" — matches deleteAccount() in
 *      lambda/user-profile, which cancels the Stripe subscription
 *      IMMEDIATELY because it's about to delete the record that would let us
 *      honor the rest of the period. If that ever becomes
 *      cancel_at_period_end, this clause has to change with it.
 *   §7 "we may limit trials to one per organization" — deliberately soft,
 *      because deleting an account and signing up again currently does earn
 *      a fresh 14-day trial and there's no suppression list (building one
 *      would mean retaining the data we just promised to erase).
 *
 * Not lawyer-reviewed. Worth a professional read before this matters.
 */

const OPERATOR = "Meng Yan Hou";
const CONTACT = "support@anthurium.sh";
const PROVINCE = "British Columbia";
const LAST_UPDATED = "24 September 2026";

const ulClass = "mb-4 list-disc space-y-2 pl-5 marker:text-rf-text-muted";

const SECTIONS = [
  {
    id: "agreement",
    title: "1. Agreement",
    body: (
      <p>
        By using Anthurium you agree to these terms. If you're accepting them
        for a company or organization, you're confirming you have the authority
        to do so on its behalf. If you don't agree, please don't use the
        service.
      </p>
    ),
  },
  {
    id: "who",
    title: "2. Who provides Anthurium",
    body: (
      <p>
        Anthurium is provided by {OPERATOR}, an individual based in {PROVINCE},
        Canada, operating under the name "Anthurium". It is not an
        incorporated company. Contact:{" "}
        <a href={`mailto:${CONTACT}`} className="text-rf-accent hover:underline">
          {CONTACT}
        </a>
        .
      </p>
    ),
  },
  {
    id: "account",
    title: "3. Your account",
    body: (
      <p>
        You need a GitHub account to use Anthurium. You're responsible for
        activity that happens under your account, and for keeping your GitHub
        account secure. Accounts are for one person — don't share them.
      </p>
    ),
  },
  {
    id: "repositories",
    title: "4. Connecting repositories",
    body: (
      <>
        {/*
          Said "read-only access" until the review write-back shipped. In a
          document the user is agreeing to, the scope of access granted has to
          be stated accurately — so this now names the single write the App
          performs rather than describing the grant as read-only.
        */}
        <p className="mb-4">
          Installing the GitHub App grants Anthurium access to read pull
          requests, repository contents, and metadata on the repositories you
          select, and to request reviewers on pull requests in them. It cannot
          modify your code. You control that selection on GitHub, and you can
          change or revoke it there at any time.
        </p>
        <p>
          <span className="font-semibold text-rf-text-primary">
            By connecting repositories you confirm that you're authorized to do
            so, and you accept responsibility for informing the people whose
            pull request activity will be processed
          </span>{" "}
          — including contributors who don't use Anthurium themselves. See our{" "}
          <Link to="/privacy" className="text-rf-accent hover:underline">
            privacy policy
          </Link>{" "}
          for what that processing involves.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "5. Acceptable use",
    body: (
      <>
        <p className="mb-4">Please don't:</p>
        <ul className={ulClass}>
          <li>Try to access another team's data</li>
          <li>Reverse engineer, resell, or rebrand the service</li>
          <li>Abuse the API with automated or excessive traffic</li>
          <li>Use Anthurium in a way that breaks GitHub's own terms, or the law</li>
        </ul>
        <p>
          We may suspend accounts that do these things, usually after telling
          you why.
        </p>
      </>
    ),
  },
  {
    id: "billing",
    title: "6. Plans, billing, and cancellation",
    body: (
      <>
        <p className="mb-4">
          The Free plan covers 1 repository and 5 members. Pro is $99 USD per
          month, or annually at a discount, and covers your whole team.
        </p>
        <p className="mb-4">
          Payments are processed by Stripe. Subscriptions renew automatically
          until you cancel. You can cancel any time from the billing portal —{" "}
          <span className="font-semibold text-rf-text-primary">
            you keep Pro access until the end of the period you've already paid
            for
          </span>
          , and then move to the Free plan rather than losing access entirely.
        </p>
        <p className="mb-4">
          <span className="font-semibold text-rf-text-primary">
            Partial billing periods aren't refunded.
          </span>{" "}
          Deleting your account or your team sets your subscription to cancel at
          the end of the period you've already paid for, so you're never charged
          again but you don't lose time you've bought. If you restore within the
          30-day window, the subscription resumes.
        </p>
        <p>
          Prices may change with at least 30 days' notice before they affect
          you.
        </p>
      </>
    ),
  },
  {
    id: "trials",
    title: "7. Trials",
    body: (
      <p>
        New teams get 14 days of Pro automatically, with no credit card
        required. When the trial ends you move to the Free plan. We may limit
        trials to one per organization.
      </p>
    ),
  },
  {
    id: "your-content",
    title: "8. Your content",
    body: (
      <p>
        Your code and repository data remain yours. You grant us a limited
        licence to process the metadata described in the{" "}
        <Link to="/privacy" className="text-rf-accent hover:underline">
          privacy policy
        </Link>{" "}
        purely to provide the service. We don't use it to train machine learning
        models, and we don't share it with anyone outside the service providers
        listed there.
      </p>
    ),
  },
  {
    id: "availability",
    title: "9. Availability",
    body: (
      <p>
        Anthurium is provided as-is, with no uptime guarantee. It's run by one
        person, so maintenance windows and occasional downtime are realistic.
        Features may change. If we ever discontinue the service entirely, we'll
        give you reasonable notice and time to export what you need.
      </p>
    ),
  },
  {
    id: "third-parties",
    title: "10. Third-party dependencies",
    body: (
      <p>
        Anthurium depends on GitHub, Amazon Web Services, Neon, and Stripe.
        Outages, changes, or restrictions at any of them can affect Anthurium,
        and those are outside our control.
      </p>
    ),
  },
  {
    id: "disclaimer",
    title: "11. Disclaimer",
    body: (
      <>
        <p className="mb-4">
          Anthurium is provided without warranties of any kind, express or
          implied, including fitness for a particular purpose.
        </p>
        <p>
          <span className="font-semibold text-rf-text-primary">
            Complexity scores, priorities, and reviewer assignments are advisory
            signals, not guarantees.
          </span>{" "}
          They are not a substitute for human code review, and they say nothing
          definitive about whether code is correct, secure, or fit to ship. You
          remain responsible for reviewing your own code.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "12. Limitation of liability",
    body: (
      <p>
        To the extent permitted by applicable law, our total liability to you
        for any claim relating to Anthurium is limited to the amount you paid
        us in the 12 months before the claim, or CAD $100 if you paid nothing.
        We're not liable for indirect, incidental, or consequential damages,
        including lost profits or lost data. Some consumer protection laws don't
        allow these limits, in which case they apply only as far as the law
        allows.
      </p>
    ),
  },
  {
    id: "indemnity",
    title: "13. Indemnity",
    body: (
      <p>
        You agree to cover us against claims arising from your misuse of
        Anthurium, or from connecting repositories you weren't authorized to
        connect.
      </p>
    ),
  },
  {
    id: "termination",
    title: "14. Termination",
    body: (
      <p>
        You can stop using Anthurium at any time by deleting your account and
        uninstalling the GitHub App. We may suspend or terminate access for
        breach of these terms. On termination your right to use the service
        ends; what happens to your data is described in the{" "}
        <Link to="/privacy" className="text-rf-accent hover:underline">
          privacy policy
        </Link>
        .
      </p>
    ),
  },
  {
    id: "changes",
    title: "15. Changes to these terms",
    body: (
      <p>
        We may update these terms. The "last updated" date above will change,
        and for material changes we'll give notice in the app or by email.
        Continuing to use Anthurium after that means you accept the updated
        terms.
      </p>
    ),
  },
  {
    id: "governing-law",
    title: "16. Governing law",
    body: (
      <p>
        These terms are governed by the laws of the Province of {PROVINCE} and
        the federal laws of Canada that apply there. The courts of {PROVINCE}{" "}
        have jurisdiction over any dispute, subject to any consumer protection
        rights you have to bring a claim where you live.
      </p>
    ),
  },
  {
    id: "contact",
    title: "17. Contact",
    body: (
      <p>
        {OPERATOR} — {PROVINCE}, Canada
        <br />
        <a href={`mailto:${CONTACT}`} className="text-rf-accent hover:underline">
          {CONTACT}
        </a>
      </p>
    ),
  },
];

export default function TermsPage() {
  return (
    <div className="overflow-x-hidden bg-rf-bg-primary">
      <MarketingNav />

      <Section className="!pb-8">
        <SectionHeading
          eyebrow="Legal"
          title="Terms of Service"
          lead={`Last updated ${LAST_UPDATED}`}
        />
      </Section>

      {SECTIONS.map((section, i) => (
        <Section
          key={section.id}
          id={section.id}
          variant={i % 2 === 1 ? "elevated" : "default"}
          className="!py-12 md:!py-16"
        >
          <div className="mx-auto max-w-[68ch]">
            <h2 className="mb-4 text-2xl font-bold tracking-tight text-rf-text-primary">
              {section.title}
            </h2>
            <div className="text-sm leading-relaxed text-rf-text-secondary">{section.body}</div>
          </div>
        </Section>
      ))}

      <MarketingFooter />
    </div>
  );
}
